import { z } from 'zod';
import { KeenOutcome, NodeKind, Segment, SpeakerMap } from './domain.js';

// Internal worker API payloads (Authorization: Bearer <WORKER_TOKEN>).

export const WorkerJobType = z.enum(['transcribe']);
export type WorkerJobType = z.infer<typeof WorkerJobType>;

export const ClaimJobRequest = z.object({
  type: WorkerJobType,
  workerId: z.string().min(1),
});
export type ClaimJobRequest = z.infer<typeof ClaimJobRequest>;

// 204 when there is no job; otherwise this body.
// audioUrl serves chunk 0; when chunkCount > 1, audioUrlTemplate contains "{seq}".
export const ClaimJobResponse = z.object({
  jobId: z.uuid(),
  recordingId: z.uuid(),
  audioUrl: z.string().url(),
  audioUrlTemplate: z.string().optional(),
  chunkCount: z.number().int().positive(),
  mime: z.string(),
  hints: z.object({ language: z.string().default('en') }),
  attempt: z.number().int().positive().optional(),
  leaseSeconds: z.number().int().positive().optional(),
});
export type ClaimJobResponse = z.infer<typeof ClaimJobResponse>;

export const JobResultRequest = z.object({
  attempt: z.number().int().positive().optional(),
  language: z.string().min(1),
  segments: z.array(Segment),
  speakerMap: SpeakerMap.optional(),
  asrModel: z.string().min(1),
  diarizationModel: z.string().optional(),
  durationS: z.number().nonnegative().optional(),
});
export type JobResultRequest = z.infer<typeof JobResultRequest>;

export const JobFailRequest = z.object({
  attempt: z.number().int().positive().optional(),
  error: z.string(),
  retryable: z.boolean(),
});
export type JobFailRequest = z.infer<typeof JobFailRequest>;

export const JobLeaseRequest = z.object({ attempt: z.number().int().positive() });

export const JobAckResponse = z.object({
  ok: z.literal(true),
  status: z.enum(['done', 'queued', 'failed']),
  attempts: z.number().int().nonnegative().optional(),
});
export type JobAckResponse = z.infer<typeof JobAckResponse>;

export const HeartbeatRequest = z.object({
  nodeId: z.string().min(1),
  kind: NodeKind,
  services: z.record(z.string(), z.unknown()),
  versions: z.record(z.string(), z.unknown()),
  imageDigest: z.string().optional(),
});
export type HeartbeatRequest = z.infer<typeof HeartbeatRequest>;

export const HeartbeatResponse = z.object({ ok: z.literal(true), receivedAt: z.string() });
export type HeartbeatResponse = z.infer<typeof HeartbeatResponse>;

// GET /api/internal/keen-export (Authorization: Bearer <WORKER_TOKEN>).
// Labelled examples for the interjection classifier fine-tune. A user turn judged in keen mode
// is a positive when the decider said yes or a "Reply anyway" override produced a reply; a
// judged turn the decider stayed silent on with no reply is a negative.
export const KeenExportSample = z.object({
  conversationId: z.uuid(),
  turnId: z.uuid(),
  /** Actual turn sequence. A prior mentioned reply may be absent from this judged-only export. */
  seq: z.number().int().positive(),
  /** The complete current user message, before recentTranscript's speaker prefix and six-turn window. */
  content: z.string().min(1),
  transcript: z.string().min(1),
  /** Inputs used by the default-off text pre-gate. The roster is captured when exported; re-export if it changes. */
  gateInputs: z.object({ otherMemberNames: z.array(z.string()), recentAssistant: z.boolean(), hasMention: z.boolean() }),
  shouldInterject: z.boolean(),
  /** The decider's probability; null when it gave none (a timeout, an unreachable engine: see outcome). */
  p: z.number().min(0).max(1).nullable(),
  /** 'decider' when the label follows the decider's own verdict; 'override' when a person's Reply anyway flipped it;
   *  'none' when the decider gave no verdict and nobody overrode it (not a training label, a moment to label). */
  labelSource: z.enum(['decider', 'override', 'none']),
  /** What became of the decision (turns.keen_outcome); null on turns judged before it was recorded. */
  outcome: KeenOutcome.nullable(),
});
export type KeenExportSample = z.infer<typeof KeenExportSample>;

export const KeenExportResponse = z.object({
  samples: z.array(KeenExportSample),
  /** Offset to pass to the next page, or null when done. */
  nextOffset: z.number().int().nonnegative().nullable(),
});
export type KeenExportResponse = z.infer<typeof KeenExportResponse>;

/** One decision opportunity in a recorded meeting, rebuilt from its transcript (workers/decision replay). */
export const MeetingMoment = z.object({
  meetingId: z.uuid(),
  segmentIndex: z.number().int().nonnegative(),
  /** The meeting's listen mode now; it can have changed during the meeting. */
  listenMode: z.enum(['always', 'mentions', 'keen', 'silent']),
  speaker: z.string().min(1),
  start: z.number().nonnegative(),
  end: z.number().nonnegative(),
  /** Seconds of silence since the previous segment ended; null for the first segment. */
  gapBeforeS: z.number().nonnegative().nullable(),
  wordCount: z.number().int().nonnegative(),
  /** Says Jabberr's name: the mention bypass answers without a decision. */
  mention: z.boolean(),
  /** A backchannel ("yeah", "thanks"): the managed voice path skips the decision. */
  backchannel: z.boolean(),
  /** What the meeting decider is sent for this segment: segments up to and including it, last 4000 characters. */
  transcript: z.string().min(1),
  /** Segments after this one until Jabberr spoke, when he did within the next three; null otherwise. */
  jabberrSpokeWithin: z.number().int().positive().nullable(),
  /** The live meeting decider's P(yes) and outcome for this segment, from its keen_decision event; null when
   *  none ran or it was recorded before the event existed. */
  deciderP: z.number().min(0).max(1).nullable().optional(),
  deciderOutcome: KeenOutcome.nullable().optional(),
});
export type MeetingMoment = z.infer<typeof MeetingMoment>;

export const MeetingMomentsResponse = z.object({
  moments: z.array(MeetingMoment),
  nextOffset: z.number().int().nonnegative().nullable(),
});
export type MeetingMomentsResponse = z.infer<typeof MeetingMomentsResponse>;
